import styles from "./minimum-payment.module.css"
import React from "react"
import * as recharts from "recharts"
import cx from "classnames"
import * as util from "activities/util"
import * as fonts from "shared/fonts"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import * as chartUtils from "activities/components/charts/util"
import * as finance from "shared/finance"
import assign from "object-assign"
import AccessibilityTable from "activities/components/shared/accessibility-table"

const monthlyPaymentMin = (balance, percentage) => (balance * percentage > 15 ? balance * percentage : 15)

const reduceOneMonthMin = (balance, rate, percentage) => balance * (rate + 1) - monthlyPaymentMin(balance, percentage)
const reduceOneMonthFixed = (balance, rate, payment) => balance * (rate + 1) - payment
const getMonthsToPayoffMinimum = (b, r, p) => {
  let months = 0
  let balance = b
  let newBalance = null
  while (balance > 0 && months < 900) {
    newBalance = reduceOneMonthMin(balance, r, p)
    months++
    if (newBalance > balance) {
      return 120
    } else {
      balance = newBalance
    }
  }
  return months
}

const checkPayment = (balance, minimumPayment) => (balance >= minimumPayment ? minimumPayment : balance)

const totalInterestPaidMinimum = (balance, rate, percent, months) => {
  const tup = [...Array(months)].reduce(
    (memo, months) => {
      const [b, p, t] = memo
      const newBalance = b < 15 ? b - p : reduceOneMonthMin(b, rate, percent)
      const newPayment = checkPayment(newBalance, monthlyPaymentMin(newBalance, percent))
      memo[0] = newBalance
      memo[1] = newPayment
      memo[2] = t + newPayment
      return memo
    },
    [balance, balance * percent, 400]
  )
  // => [current balance, payment for the month, running total of payments]
  return Math.max(tup[2] - balance, 0)
}

// change chart data so line for fixedBalance stops after the first zero value
const updateValsForNoLine = (data) => {
  let pastFirstZero = false
  return data.map((year) => {
    if (year.fixedBalance <= 0 && pastFirstZero === false) {
      pastFirstZero = true
    } else if (year.fixedBalance <= 0 && pastFirstZero === true) {
      year.fixedBalance = null
    }
    return year
  })
}

const generateChartData = (startingAmount, rate, minimum, fixed, monthsToPayoff) => {
  return [...Array(monthsToPayoff)].reduce(
    (memo, x) => {
      const updating = memo[memo.length - 1]
      const {fixedBalance, minimumBalance, year, counter} = updating
      const newMinimumBal = reduceOneMonthMin(minimumBalance, rate, minimum)
      const newFixedBal = reduceOneMonthFixed(fixedBalance, rate, fixed)
      if (counter < 12) {
        updating["counter"] = counter + 1
        updating["minimumBalance"] = util.ifNegative(newMinimumBal)
        updating["fixedBalance"] = util.ifNegative(newFixedBal)
        memo[memo.length - 1] = updating
      } else {
        const newItem = {
          year: year + 1,
          counter: 1,
          fixedBalance: newFixedBal,
          minimumBalance: newMinimumBal
        }
        memo.push(newItem)
      }
      return memo
    },
    [
      {
        year: 0,
        counter: 0,
        fixedBalance: startingAmount,
        minimumBalance: startingAmount
      },
      {
        year: 1,
        counter: 1,
        fixedBalance: reduceOneMonthFixed(startingAmount, rate, fixed),
        minimumBalance: reduceOneMonthMin(startingAmount, rate, minimum)
      }
    ]
  )
}

const formatTooltipValue = (year, key) => (year ? util.formatDollars(util.ifNegative(year[key])) : util.formatDollars(0))

// TODO: adapt this chart to use the line chart component
// then we can use that tooltip instead
export const Tooltip = ({payload, theme}) => {
  const year = payload.length > 0 ? payload[0].payload : null
  return (
    <div>
      <div className={cx(styles["tooltip"], fonts.getFontClass())}>
        <span>Year:</span>
        <span className={styles["value"]}>{year ? year.year : ""}</span>
      </div>
      <div className={cx(styles["tooltip"], fonts.getFontClass())}>
        <span className={styles["with-indicator"]}>
          <div style={{background: "#5c5761"}} className={styles["indicator"]} />
          Minimum:
        </span>
        <span className={styles["value"]}>{chartUtils.formatTooltipValueVersion2(year, "minimumBalance")}</span>
      </div>
      <div className={cx(styles["tooltip"], fonts.getFontClass())}>
        <span className={styles["with-indicator"]}>
          <div style={{background: "#" + theme["color-1"]}} className={styles["indicator"]} />
          Fixed:
        </span>
        <span className={styles["value"]}>{chartUtils.formatTooltipValueVersion2(year, "fixedBalance")}</span>
      </div>
    </div>
  )
}

const calcMinimumPayment = ({balance, minimumPayment, fixedPayment, rate}) => {
  const decimalRate = rate / 100
  const monthlyInterestRate = util.getMonthlyRate(decimalRate)
  const minimumPercent = minimumPayment / balance
  const monthsToPayoffMinimum = getMonthsToPayoffMinimum(balance, monthlyInterestRate, minimumPercent)
  const monthsToPayoffFixed = finance.calcMonthsToPayoffFixedLoan(balance, monthlyInterestRate, fixedPayment)
  const payoffDate = util.getPayoffDateObject(monthsToPayoffFixed)
  const canBePaidOffFixed = finance.canBePaidOff({balance, rate: decimalRate, payment: fixedPayment})
  const fixedSchedule = canBePaidOffFixed ? finance.genFixedPaymentSchedule(balance, decimalRate, fixedPayment) : []
  const totalPaidFixed = canBePaidOffFixed ? fixedPayment * (monthsToPayoffFixed - 1) + fixedSchedule[fixedSchedule.length - 2] : 0
  const totalInterestFixed = canBePaidOffFixed ? totalPaidFixed - balance : 0
  return {
    monthlyInterestRate,
    minimumPercent,
    monthsToPayoffMinimum,
    totalPaidFixed,
    totalInterestFixed,
    payoffDate
  }
}

const show = (payoffDate, val, alt) => (payoffDate.month ? val : alt)

const showFull = (payoffDate) => show(payoffDate, `${payoffDate.month} ${payoffDate.year}`, "Not Possible")

const interestSaved = (balance, monthlyInterestRate, minimumPercent, monthsToPayoffMinimum, totalInterestFixed) =>
  totalInterestPaidMinimum(balance, monthlyInterestRate, minimumPercent, monthsToPayoffMinimum) - totalInterestFixed

export default shared.ActivityFactory(
  ({getProps, getState, updateState}) => {
    const state = getState()
    const {balance, minimumPayment, fixedPayment, rate, resetKey} = state
    const {id, theme} = getProps()
    const {monthlyInterestRate, minimumPercent, monthsToPayoffMinimum, totalPaidFixed, totalInterestFixed, payoffDate} = calcMinimumPayment(state)
    const data = updateValsForNoLine(generateChartData(balance, monthlyInterestRate, minimumPercent, fixedPayment, monthsToPayoffMinimum))
    const handleUpdateMin = (updateState, keys) => (amount) => keys.forEach((key) => updateState(key, util.fromString)(amount))
    const handleClick = (amount) => {
      if (fixedPayment >= 0 && amount >= 0) updateState("fixedPayment", util.fromString)(amount)
    }

    const accessibilityTableHeadings = [
      {
        name: "Years",
        key: "year"
      },
      {
        name: "Minimum Payment",
        key: "minimumBalance"
      },
      {
        name: "Fixed Payment",
        key: "fixedBalance"
      }
    ]

    const accessibilityTableData = data.map((row) => ({
      year: row.year,
      minimumBalance: util.formatDollarsNoCents(row.minimumBalance),
      fixedBalance: util.formatDollarsNoCents(row.fixedBalance)
    }))

    const accessibilityTableLabel = "Minimum Payment Comparison"

    return (
      <div>
        <shared.SummaryCalcContainer>
          <div className={styles["inputs-grid"]}>
            <inputs.SmallCalcInputWithTitle
              id={id}
              title="Current Balance"
              includeIcon={false}
              onChange={updateState("balance", util.fromString)}
              initialValue={balance}
              resetKey={resetKey}
            />
            <inputs.SmallCalcInputWithTitle
              id={id}
              title="Current Mo Payment"
              includeIcon={false}
              onChange={handleUpdateMin(updateState, ["minimumPayment", "fixedPayment"])}
              initialValue={minimumPayment}
              resetKey={resetKey}
            />
            <inputs.Input id={id} title="Interest Rate" onChange={updateState("rate")} value={rate} valueType="percent" />
            <shared.Note>See how paying above the minimum at a fixed rate affects your payoff schedule:</shared.Note>
            <div>
              <inputs.SmallCalcInputWithTitle
                id={id}
                title="Fixed Monthly Payment"
                includeIcon={false}
                onChange={updateState("fixedPayment", util.fromString)}
                initialValue={fixedPayment}
                resetKey={resetKey}
              />
              <div className={styles["buttons"]}>
                <label className="sr-only">Use the buttons below to increase or decrease the fixed monthly payment.</label>
                <shared.SmallButton onClick={() => handleClick(fixedPayment + 100)} label="Increase by 100">
                  +
                </shared.SmallButton>
                <shared.SmallButton onClick={() => handleClick(fixedPayment - 100)} label="Decrease by 100">
                  -
                </shared.SmallButton>
              </div>
            </div>
          </div>
          <shared.ChartContainer>
            <div
              className={styles["chart"]}
              role="img"
              aria-label="This line graph visually represents how much sooner you could pay off your debt by paying more than the minimum. Read below to find the pay-off date, the total amount paid, the total interest to be paid, and the how much you saved on interest payments."
            >
              <chartUtils.AxisLabel label="Balance" axis="yaxis" />
              <recharts.ResponsiveContainer width={"100%"} height={"100%"}>
                <recharts.LineChart data={data}>
                  <recharts.XAxis
                    dataKey="year"
                    interval={0}
                    axisLine={false}
                    tickLine={false}
                    ticks={util.getScreenWidth() < 620 || data.length > 35 ? chartUtils.getXAxisTicks("year", data) : []}
                    tick={{fontSize: "12", fontFamily: fonts.getFontFamily(), letterSpacing: ".13em", fill: "#5c5761", dy: 8}}
                  />
                  <recharts.YAxis
                    type="number"
                    domain={[0, "dataMax"]}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(tick) => util.formatDollarsNoCents(tick)}
                    tick={{fontSize: "12", fontFamily: fonts.getFontFamily(), fill: "#5c5761", dx: -4}}
                  />
                  <recharts.Line
                    type="monotone"
                    dataKey="minimumBalance"
                    stroke={"#5c5761"}
                    strokeWidth={2}
                    isAnimationActive={false}
                    activeDot={{stroke: "#5c5761", strokeWidth: 2, r: 4}}
                  />
                  <recharts.Line
                    type="monotone"
                    dataKey="fixedBalance"
                    stroke={"#" + theme["color-1"]}
                    strokeWidth={2}
                    activeDot={{stroke: "#" + theme["color-1"], strokeWidth: 2, r: 4}}
                    isAnimationActive={false}
                  />
                  <recharts.Tooltip cursor={false} content={<Tooltip theme={theme} />} wrapperStyle={chartUtils.tooltipStyles(3)} />
                </recharts.LineChart>
              </recharts.ResponsiveContainer>
              <chartUtils.AxisLabel label="Years" axis="xaxis" />
            </div>
          </shared.ChartContainer>
          <shared.SummaryContainer>
            <callouts.Callout>
              <callouts.Detail label="Payoff Date" result={showFull(payoffDate)} dataTestId="payoff" />
              <callouts.Detail label="Total Amount Paid" result={show(payoffDate, util.formatDollarsNoCents(totalPaidFixed), "Unknown")} dataTestId="total-paid" />
              <callouts.Detail label="Total Interest Paid" result={show(payoffDate, util.formatDollarsNoCents(totalInterestFixed), "Unknown")} dataTestId="interest-paid" />
              <callouts.Detail
                label="Interest Saved"
                result={show(
                  payoffDate,
                  util.formatDollarsNoCents(interestSaved(balance, monthlyInterestRate, minimumPercent, monthsToPayoffMinimum, totalInterestFixed)),
                  "Unknown"
                )}
                dataTestId="interest-saved"
              />
            </callouts.Callout>
          </shared.SummaryContainer>
        </shared.SummaryCalcContainer>

        <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
      </div>
    )
  },
  {
    stateFilter: (state) => {
      const {payoffDate, totalPaidFixed, totalInterestFixed, monthlyInterestRate, minimumPercent, monthsToPayoffMinimum} = calcMinimumPayment(state)
      const {balance} = state
      return assign(state, {
        payoffDate: showFull(payoffDate),
        totalAmountPaid: util.round(totalPaidFixed),
        totalInterestPaid: util.round(totalInterestFixed),
        interestSaved: util.round(interestSaved(balance, monthlyInterestRate, minimumPercent, monthsToPayoffMinimum, totalInterestFixed))
      })
    },
    defaultProps: {
      id: "minimum-payment",
      className: "minimum-payment",
      title: "Minimum Payment Calculator",
      subtitle: "Explore how much you could save by paying more than your minimum payment."
    },
    defaultState: {
      balance: 10000,
      minimumPayment: 400,
      fixedPayment: 400,
      rate: 16.9
    }
  }
)
